import React, { useState } from "react";
import { Box, Paper, Stack, Typography, Chip } from "@mui/material";
import {
  DndContext, DragOverlay, useDraggable, useDroppable,
  PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import { Loader, EmptyState } from "./Common";

function DefaultCard({ ticket }) {
  return (
    <>
      <Typography variant="caption" color="text.secondary">{ticket.ticket_number}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{ticket.subject}</Typography>
    </>
  );
}

function TicketCard({ ticket, renderCard, disabled }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `ticket-${ticket.id}`,
    data: { ticket },
    disabled,
  });

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      {...listeners}
      {...attributes}
      sx={{
        p: 1.25,
        mb: 1,
        cursor: disabled ? "default" : "grab",
        opacity: isDragging ? 0.3 : 1,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        "&:hover": { boxShadow: 1 },
      }}
    >
      {renderCard ? renderCard(ticket) : <DefaultCard ticket={ticket} />}
    </Paper>
  );
}

function Column({ status, tickets, renderCard, activeTicket, isDropAllowed }) {
  const dropAllowed = activeTicket
    ? !isDropAllowed || activeTicket.status_id === status.id || isDropAllowed(activeTicket.status_id, status.id)
    : true;

  const { setNodeRef, isOver } = useDroppable({
    id: `status-${status.id}`,
    data: { statusId: status.id },
  });

  const highlight = activeTicket && isOver
    ? (dropAllowed ? "rgba(56, 142, 60, 0.12)" : "rgba(176, 0, 32, 0.10)")
    : undefined;
  const borderColor = activeTicket && isOver ? (dropAllowed ? "#388E3C" : "#B00020") : undefined;

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={{
        width: 300, flexShrink: 0, display: "flex", flexDirection: "column",
        maxHeight: "100%", bgcolor: highlight, borderColor, transition: "background-color 0.1s, border-color 0.1s",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.5, pb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{status.name}</Typography>
        <Chip size="small" label={tickets.length} />
      </Stack>
      <Box sx={{ px: 1.5, pb: 1.5, overflowY: "auto", flex: 1, minHeight: 80 }}>
        {tickets.length === 0 && (
          <Typography variant="caption" color="text.secondary">No tickets</Typography>
        )}
        {tickets.map((t) => (
          <TicketCard key={t.id} ticket={t} renderCard={renderCard} disabled={Boolean(activeTicket)} />
        ))}
      </Box>
    </Paper>
  );
}

/**
 * columns: [{ id, name, state_group }] ordered by sort_order
 * cardsByColumn: { [statusId]: Ticket[] }
 * onCardDrop: (ticketId, fromStatusId, toStatusId) => void
 * isDropAllowed?: (fromStatusId, toStatusId) => boolean -- client-side UX
 *   pre-check only; the server's transition validation is the real gate.
 * renderCard?: (ticket) => node
 */
export default function KanbanBoard({ columns, cardsByColumn, onCardDrop, isDropAllowed, renderCard, loading }) {
  const [activeTicket, setActiveTicket] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  if (loading) return <Loader />;
  if (!columns || columns.length === 0) {
    return <EmptyState title="No statuses configured" subtitle="Add a status from Admin → Workflow to start using the board" />;
  }

  const handleDragStart = (event) => {
    const ticket = event.active.data.current?.ticket;
    setActiveTicket(ticket || null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveTicket(null);
    if (!over) return;
    const ticket = active.data.current?.ticket;
    const toStatusId = over.data.current?.statusId;
    if (!ticket || !toStatusId || ticket.status_id === toStatusId) return;
    if (isDropAllowed && !isDropAllowed(ticket.status_id, toStatusId)) return;
    onCardDrop(ticket.id, ticket.status_id, toStatusId);
  };

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setActiveTicket(null)}>
      <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 1, height: "calc(100vh - 260px)" }}>
        {columns.map((status) => (
          <Column
            key={status.id}
            status={status}
            tickets={cardsByColumn[status.id] || []}
            renderCard={renderCard}
            activeTicket={activeTicket}
            isDropAllowed={isDropAllowed}
          />
        ))}
      </Box>
      <DragOverlay>
        {activeTicket && (
          <Paper variant="outlined" sx={{ p: 1.25, width: 280, boxShadow: 4 }}>
            {renderCard ? renderCard(activeTicket) : <DefaultCard ticket={activeTicket} />}
          </Paper>
        )}
      </DragOverlay>
    </DndContext>
  );
}
